// ============================================================
// TERROR ENGINE — PLAZA ZONES
// One table. Every collision box and every interact area in the
// Plaza, for both orientations, derived from ONE set of numbers.
// ============================================================
//
// WHY THIS FILE EXISTS (caP'n, July 29 2026 — the new Plaza art)
//
// The old geometry lived in six parallel places: PORTALS, SHOPS,
// NPCS, their three P_* portrait twins, and a buildMap() that
// painted tile TYPES over the top. Every object described itself
// with SIX fields — `x`, `y`, `rx`, `ry`, plus optional `minX`,
// `maxX`, `minY`, `maxY` clamps bolted on whenever a radius
// overshot into a neighbour. A centred radius plus four clamps is
// a rectangle written the hard way, and it could only ever be ONE
// rectangle, which is why the fountain was walkable: there was no
// way to say "solid in the middle, reachable from all four sides."
//
// THE FIX IS THE OBVIOUS ONE — say what you mean. Every object
// carries two lists of plain rectangles:
//
//   solid — tiles the player cannot stand on
//   zone  — tiles that raise this object's prompt
//
// Lists, not single rects, because the tent is an L and the
// fountain is a ring. Rectangles, not radii, because they were
// measured directly off the painting. And the two are INDEPENDENT,
// which is the whole point: a fountain is a wall you talk to, and
// that sentence was previously unsayable.
//
// PORTRAIT IS DERIVED, NEVER AUTHORED. The old P_* lists were a
// hand-maintained second copy, and two copies of a layout drift —
// that is a law in `ie-room-chrome` written in someone's lost
// afternoon. Here the portrait grid is the landscape table run
// through `scaleRect()`. Move a box once and both orientations
// move. There is no second copy to forget.
//
// EVERY NUMBER BELOW WAS MEASURED, NOT GUESSED. The boxes come
// from a connected-component pass over `plaza_1.webp` — each
// object's true bounding box in image pixels, converted straight
// into this 34x20 grid. Screenshots are ground truth for geometry;
// this time the screenshot was the instrument.

import { GM, SD } from "./theme";

// ── GRID ──────────────────────────────────────────────────────
export const COLS = 34;
export const ROWS = 20;
export const P_COLS = 20;
export const P_ROWS = 12;

const SX = P_COLS / COLS;   // 0.588…
const SY = P_ROWS / ROWS;   // 0.6

// Rect format is [x0, y0, x1, y1], INCLUSIVE on all four sides.
// Inclusive because these were read off a picture, and "columns 12
// through 21" is how a human reads a picture. Exclusive bounds are
// for loops, not for authoring.
const inRect = (x, y, [x0, y0, x1, y1]) => x >= x0 && x <= x1 && y >= y0 && y <= y1;
const inAny = (x, y, rects) => !!rects && rects.some(r => inRect(x, y, r));

// Landscape rect → portrait rect, and the two directions round
// DIFFERENTLY on purpose.
//
// THE ASYMMETRY IS THE WHOLE TRICK. First attempt grew both, on the
// reasoning that a solid which shrank might open a hole in a wall.
// The flood fill said otherwise, immediately and without mercy: at
// 20x12 the eleven objects are dense enough that growing every
// solid by a tile welded Willy, the fountain and V'yen into one
// unbroken bar across row 5, severing the entire portal shelf from
// the floor. 36 reachable tiles out of 240 and seven dead doors.
//
// So round each way toward the failure you can live with:
//
//   solid → ROUND. Never grows. Too-small means you can clip the
//           corner of the tent. Too-big means the room is sealed
//           and the game is over. These are not comparable.
//   zone  → GROW.  Never shrinks. Too-big means a prompt appears a
//           tile early and the console cycles it. Too-small means a
//           door you can see and cannot open.
//
// Both directions err toward "the room still works," which is the
// only property worth protecting under a lossy transform.
const shrinkRect = ([x0, y0, x1, y1]) => {
  const nx0 = Math.round(x0 * SX), ny0 = Math.round(y0 * SY);
  return [
    nx0, ny0,
    Math.max(nx0, Math.round((x1 + 1) * SX) - 1),
    Math.max(ny0, Math.round((y1 + 1) * SY) - 1),
  ];
};
const growRect = ([x0, y0, x1, y1]) => {
  const nx0 = Math.floor(x0 * SX), ny0 = Math.floor(y0 * SY);
  return [
    nx0, ny0,
    Math.max(nx0, Math.ceil((x1 + 1) * SX) - 1),
    Math.max(ny0, Math.ceil((y1 + 1) * SY) - 1),
  ];
};
const scaleSolids = (rects) => (rects || []).map(shrinkRect);
const scaleZones  = (rects) => (rects || []).map(growRect);

// ── TERRAIN ───────────────────────────────────────────────────
// The room's shell. Rows 0-4 are the PORTAL SHELF — in the art the
// five arches stand on top of the wall, outside the room, which is
// why the player can never walk up there. They are reached from the
// floor strip at row 5, the open highway that runs the full width.
const TERRAIN_SOLID = [
  [0, 0, COLS - 1, 4],            // portal shelf + top wall
  [0, ROWS - 1, COLS - 1, ROWS - 1], // bottom wall
  [0, 5, 0, ROWS - 2],            // left wall
  [COLS - 1, 5, COLS - 1, ROWS - 2], // right wall
];

// ── PORTAL COLOURS ────────────────────────────────────────────
// Sampled from plaza_1.webp (top 3% by saturation x brightness in
// each arch), then lifted to a legible UI accent. Dials.
export const PORTAL_COLORS = {
  purple: "#a855f7",
  gold:   "#e8c46f",
  blue:   "#5b83e8",
  red:    "#c2364f",
  green:  "#5fd94d",
};

// ── THE LOCKED LINE ───────────────────────────────────────────
// caP'n's words, verbatim. Two doors are painted and lit but lead
// nowhere yet; they say so in character rather than being dark.
export const AENERGY_LOCKED = "The Aenergy current destroyed the path... for now.";

// ── THE TABLE ─────────────────────────────────────────────────
// kind    "portal" | "shop" | "npc" | "landmark" — chooses the lane
//         the console prompt lives in, nothing more.
// verb    the console's action word. DATA, not a ternary chain.
//         The old code had three stacked `id === "x" ? … :` ladders
//         and every new object meant editing all three.
// route   portals only. Where the door goes. App.js switches on
//         THIS, never on an id — see the note in Plaza.js.
// locked  a string means the door is painted but shut, and the
//         string is what it says. Absence means it opens.
export const ZONES = [
  // ══ PORTAL SHELF, left to right, matching the painting ══════
  {
    id: "emperiums", kind: "portal", verb: "Enter",
    label: "The Mineriums", subtitle: "Sealed",
    color: PORTAL_COLORS.purple,
    locked: AENERGY_LOCKED,
    zone: [[3, 5, 8, 6]],
  },
  {
    id: "affinity", kind: "portal", verb: "Affinity",
    label: "Hall of Allegiance", subtitle: "Choose your Ilk",
    color: PORTAL_COLORS.gold,
    route: "faction",
    zone: [[9, 5, 14, 6]],
  },
  {
    id: "story", kind: "portal", verb: "Enter",
    label: "The Unstable World", subtitle: "Story Mode",
    color: PORTAL_COLORS.blue,
    route: "worldmap",
    zone: [[15, 5, 20, 6]],
  },
  {
    id: "quarters", kind: "portal", verb: "Enter",
    label: "Your Quarters", subtitle: "The Unstable Pierre",
    color: PORTAL_COLORS.red,
    locked: AENERGY_LOCKED,
    zone: [[21, 5, 26, 6]],
  },
  {
    id: "pierre", kind: "portal", verb: "Cross Over",
    label: "The Unstable Pierre", subtitle: "Cross-Game Hub",
    color: PORTAL_COLORS.green,
    route: "pierre",
    zone: [[27, 5, 32, 6]],
  },

  // ══ THE FLOOR ═══════════════════════════════════════════════
  {
    // Moved to the top-left wall in the new art. Was at (24,11).
    id: "bulletin", kind: "npc", verb: "Notices",
    label: "Bulletin Board", color: GM.inkGold,
    solid: [[2, 6, 4, 7]],
    zone:  [[1, 5, 5, 8]],
  },
  {
    id: "herald", kind: "npc", verb: "Speak",
    label: "Willy", color: GM.inkGold,
    solid: [[6, 7, 11, 8]],
    zone:  [[5, 6, 12, 9]],
  },
  {
    // V'yen kept her corner and opened a stall in it. Her measured
    // box lands within half a tile of where she already stood, so
    // she did not move — she went into business.
    //
    // STAIRSTEPPED (caP'n, July 30 2026). Same disease as the tent:
    // the stall's mass is up and to the RIGHT — sign, counter,
    // V'yen herself — and the serpent coil only sweeps down-left,
    // thinning as it goes. The box's bottom-left corner was empty
    // wall you could see and not walk on, and it made a pinch with
    // the fountain's top-right corner that you had to squeeze
    // through. Both sides of that pinch just got a tile back.
    id: "vyren", kind: "npc", verb: "Match",
    label: "V'yen — Sweet Treats", color: SD.red,
    solid: [
      [23, 6, 32, 7],   // sign, canopy, the high coil
      [24, 8, 32, 8],   // the coil falling away
      [25, 9, 32, 9],   // counter foot
    ],
    zone: [[21, 6, 22, 10], [23, 10, 32, 10], [23, 8, 24, 9]],
  },
  {
    // THE RING. Four rects around a solid core: you may stand on
    // any side of the fountain and none of them is inside it. This
    // is the shape the old centre-plus-radius could not express.
    //
    // OCTAGON, not box (caP'n, July 30 2026). The basin is eight-
    // sided — look at the painting, the corners are chamfered — but
    // the solid was the bounding box, so all four corners claimed
    // bare stone. The top-left one is the wall caP'n hit coming down
    // off the highway; the top-right one is half of the pinch
    // between here and V'yen's stall. One tile off each corner and
    // the collision is the shape the art already is.
    id: "fountain", kind: "landmark", verb: "Toss a Coin",
    label: "V'ael's Plaza Fountain", color: "#b98cf5",
    solid: [
      [13,  9, 20,  9],   // top edge, corners cut
      [12, 10, 21, 11],   // the wide middle
      [13, 12, 20, 12],   // bottom edge, corners cut
    ],
    // The ring became one box the moment the corners opened. Zone
    // tiles that are also solid are inert — you can't stand on them
    // — so the box says exactly what the four rects said, plus the
    // four freed corners, in a quarter of the ink.
    zone: [[11, 8, 22, 13]],
  },
  {
    id: "pack_shop", kind: "shop", verb: "Shop",
    // caP'n, 2026-08-18: the shop's keeper has a name now. "Tiny's"
    // was a default that stuck; she is VENUS. Her kiosk becomes a
    // dialogue window like V'yen's when that pass lands — her and the
    // real purchasable packs on screen together, not a menu.
    label: "Venus's Card Packs", color: GM.inkGold,
    // WALK UP TO THE GLASS (caP'n, 2026-09-22: "this is closest I can get
    // to the card shop"). The kiosk is drawn 0.8 of its box, so the old
    // solid [1,16,6,18] stood a whole tile of bare stone east of the case.
    // `art` is where the kiosk is DRAWN (3D places it, and Venus, from
    // this — unchanged); `solid` is where you can't walk, now ending at
    // the case's own east edge, so you can stand at col 6 beside it.
    // AGAINST THE WALL (caP'n, 2026-09-22: "slap her up against the wall so
    // we can walk up like a normal shop"). The 2D painting could only face
    // one way; in 3D the kiosk turns 90° and backs onto the west wall, glass
    // facing east, Venus behind it — you walk straight up to her.
    // Was: art [1,16,6,18] · solid [1,16,5,18] · zone [1,15,7,15],[6,16,7,18].
    // A box taller than it is wide is what tells 3D to turn it (SculptedKiosk).
    art:   [[1, 13, 3, 18]],
    solid: [[1, 13, 3, 18]],
    zone:  [[4, 13, 5, 18]],
  },
  {
    // The tattered tent. YOU CAN GO IN NOW (caP'n, July 30 2026).
    //
    // Was one flat rect, [19,14,32,18] — the tent's whole bounding
    // box. Two things wrong with that. First, the tent is a TRIANGLE:
    // the peak is up-right and the fabric falls away to the left, so
    // the box's top-left corner claimed a wedge of bare stone you
    // could see and could not stand on. That was the wall caP'n
    // walked into. Second, the workshop's interior is PAINTED — the
    // forge fire, the bench, the racked tools are all there in the
    // art — and the box sealed it behind canvas.
    //
    // Three rects instead. The canopy is solid, the ground band
    // beneath it is not, and you enter at the tent's left edge where
    // the skirt lifts off the stone.
    //
    //   row 14   cols 21-32   the peak. Shaved two off the left; the
    //                         fabric genuinely isn't there yet.
    //   rows 15-16  cols 19-32  the canopy proper. Solid canvas.
    //   rows 17-18  cols 30-32  the tool wall and the back poles.
    //
    // Everything else at rows 17-18 is floor. Walk in from col 18,
    // carry on right, and you fetch up at the fire around col 25.
    //
    // WHY THE DOOR IS ON THE LEFT AND NOWHERE ELSE. The tent's right
    // edge is one tile off the room wall and its bottom edge IS the
    // bottom wall, so there is no south or east approach to give it.
    // A mouth cut through the canopy doesn't work either: row 13 sits
    // directly above row 14, so a gap in the roof is a door you enter
    // by walking over the tent. The left edge at ground level is the
    // only opening the room's shell allows, and it happens to be
    // where the art already lifts.
    //
    // AND IT IS THE ONLY ONE PORTRAIT SURVIVES. At 20x12 the whole
    // ground band collapses to a single row, so ANY solid on the
    // tent's lower-left seals the interior — flood fill says so, in
    // both orientations, every arrangement tried. Leave rows 17-18
    // open west of col 30 or the forge is a room nobody can reach on
    // a phone.
    id: "deck_forge", kind: "shop", verb: "Forge",
    label: "Willie's Workshop", color: GM.inkGoldBright,
    // RESIZED TO THE ROOM (caP'n, 2026-09-22): "the Forge ... literally
    // inches up to the fountain ... its so hard to walk right there".
    // The tent was the painting's size, 14 wide from col 19, and its skirt
    // reached the fountain's corner. It is 10 wide now, cols 23-32, pushed
    // into the south-east corner; the lane between it and the fountain
    // went from ONE row (row 13 at col 21) to a clear run of cols 21-22 all
    // the way down plus the whole of row 13. Same three-rect shape, same
    // door on the left, same inside rows, so portrait still reaches the
    // fire (flood fill, both orientations). ⚠ The 2D painting still shows
    // the big tent: in 2D you can now walk over its left edge. caP'n chose
    // that ("don't worry about the 2D continuity with this one").
    //   row 14      cols 25-31  the north wall of the bell
    //   rows 15-16  cols 23-32  the canopy proper
    //   rows 17-18  cols 31-32  the rack wall and the back poles
    solid: [
      [25, 14, 31, 14],
      [23, 15, 32, 16],
      [31, 17, 32, 18],
    ],
    zone: [
      [22, 13, 32, 13],   // the top approach
      [22, 14, 24, 14],   // the freed corner
      [22, 15, 22, 18],   // the left approach column (the mouth)
      [23, 17, 30, 18],   // inside — the prompt holds while you're at the fire
    ],
  },
];

// The Hall of Allegiance no longer stands on the Plaza floor — the
// new art has no building for it and its old plot at (17,17) is
// bare stone. It moved to the gold arch. The menu hub still opens
// it, so nothing became unreachable.

// ── PORTRAIT, DERIVED ─────────────────────────────────────────
export const P_ZONES = ZONES.map(z => ({
  ...z,
  solid: scaleSolids(z.solid),
  zone:  scaleZones(z.zone),
  ...(z.art ? { art: scaleSolids(z.art) } : {}),   // where it's DRAWN scales like a solid
}));
// The shell is the one thing that must NOT shrink — the outer wall
// is the boundary of the world, and a rounded-away wall is a player
// walking off the map. Terrain grows.
const P_TERRAIN_SOLID = TERRAIN_SOLID.map(growRect);

// ── SPAWN ─────────────────────────────────────────────────────
// Open floor south of the fountain, so the first thing a new
// Aenarchist sees when they load in is the thing they can play with.
export const SPAWN = { x: 16, y: 14 };
export const P_SPAWN = {
  x: Math.min(P_COLS - 2, Math.max(1, Math.round(SPAWN.x * SX))),
  y: Math.min(P_ROWS - 2, Math.max(1, Math.round(SPAWN.y * SY))),
};

// ── QUERIES ───────────────────────────────────────────────────
// Every caller passes `portrait` explicitly rather than the helper
// calling isPortrait() itself. A geometry module that reads the
// window is a geometry module that cannot be tested, and this one
// gets flood-filled in CI precisely because it can be.
export const zonesFor = (portrait) => (portrait ? P_ZONES : ZONES);
export const gridFor  = (portrait) => (portrait
  ? { cols: P_COLS, rows: P_ROWS }
  : { cols: COLS,   rows: ROWS });

/** True when the tile is blocked — by the room shell or by an object. */
export function isSolid(x, y, portrait = false) {
  const { cols, rows } = gridFor(portrait);
  if (x < 0 || y < 0 || x >= cols || y >= rows) return true;
  const terrain = portrait ? P_TERRAIN_SOLID : TERRAIN_SOLID;
  if (inAny(x, y, terrain)) return true;
  return zonesFor(portrait).some(z => inAny(x, y, z.solid));
}

export const isWalkable = (x, y, portrait = false) => !isSolid(x, y, portrait);

/** Every object whose interact area covers this tile, in table order. */
export function zonesAt(x, y, portrait = false) {
  return zonesFor(portrait).filter(z => inAny(x, y, z.zone));
}

/** First object of a given kind covering this tile, or null. */
export function zoneAt(x, y, kind, portrait = false) {
  return zonesAt(x, y, portrait).find(z => z.kind === kind) || null;
}

export const portalAt   = (x, y, p) => zoneAt(x, y, "portal",   p);
export const shopAt     = (x, y, p) => zoneAt(x, y, "shop",     p);
export const npcAt      = (x, y, p) => zoneAt(x, y, "npc",      p);
export const landmarkAt = (x, y, p) => zoneAt(x, y, "landmark", p);

export const getZone = (id) => ZONES.find(z => z.id === id) || null;

/**
 * Flood fill from spawn. Exported because the collision table is
 * hand-authored against a painting, and the one failure mode that
 * matters — a door you can see but cannot reach — is invisible by
 * inspection and obvious to a flood fill. Run it, don't squint.
 * Returns { reachable, unreachable: [zoneId], reachableTiles }.
 */
export function auditReachability(portrait = false) {
  const { cols, rows } = gridFor(portrait);
  const spawn = portrait ? P_SPAWN : SPAWN;
  const seen = new Set();
  const key = (x, y) => `${x},${y}`;
  const stack = [[spawn.x, spawn.y]];
  if (isSolid(spawn.x, spawn.y, portrait)) {
    return { reachable: false, unreachable: ["SPAWN_IS_SOLID"], reachableTiles: 0 };
  }
  while (stack.length) {
    const [x, y] = stack.pop();
    if (x < 0 || y < 0 || x >= cols || y >= rows) continue;
    if (seen.has(key(x, y))) continue;
    if (isSolid(x, y, portrait)) continue;
    seen.add(key(x, y));
    stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
  }
  const unreachable = zonesFor(portrait)
    .filter(z => !(z.zone || []).some(([x0, y0, x1, y1]) => {
      for (let y = y0; y <= y1; y++)
        for (let x = x0; x <= x1; x++)
          if (seen.has(key(x, y))) return true;
      return false;
    }))
    .map(z => z.id);
  return { reachable: unreachable.length === 0, unreachable, reachableTiles: seen.size };
}

const plazaZones = {
  COLS, ROWS, P_COLS, P_ROWS, SPAWN, P_SPAWN,
  ZONES, P_ZONES, PORTAL_COLORS, AENERGY_LOCKED,
  zonesFor, gridFor, isSolid, isWalkable, zonesAt, zoneAt,
  portalAt, shopAt, npcAt, landmarkAt, getZone, auditReachability,
};
export default plazaZones;
